<template>
  <div class="st">
    <!-- ── Header ──────────────────────────────────────────────── -->
    <header class="st-head">
      <span class="st-eyebrow">Paramètres</span>
      <h1 class="st-title">Paramètres généraux</h1>
      <p class="st-sub">Identité, coordonnées et conditions de votre société. Ces informations apparaissent sur l'application, les tickets et les factures.</p>
    </header>

    <SettingsNav />

    <!-- Loading -->
    <div v-if="loading" class="st-grid" aria-busy="true" aria-label="Chargement des paramètres">
      <div class="st-main">
        <section v-for="n in 3" :key="n" class="st-card">
          <span class="st-skel st-skel--title"></span>
          <span class="st-skel st-skel--field"></span>
          <span class="st-skel st-skel--field"></span>
        </section>
      </div>
      <aside class="st-aside"><section class="st-card"><span class="st-skel st-skel--block"></span></section></aside>
    </div>

    <div v-else-if="loadError" class="st-error" role="alert">
      <i class="ph ph-warning-circle" aria-hidden="true"></i>
      <div>
        <strong>Impossible de charger les paramètres.</strong>
        <p>Vérifiez votre connexion puis réessayez.</p>
      </div>
      <button type="button" class="btn btn-sm btn-primary" @click="load">Réessayer</button>
    </div>

    <form v-else class="st-grid" novalidate @submit.prevent="submitForm">
      <div class="st-main">
        <!-- ── Identity ─────────────────────────────────────────── -->
        <section class="st-card" aria-labelledby="st-identity">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-identification-card" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-identity">Identité de la société</h2>
              <p>Le logo et le nom affichés dans l'application et sur les documents.</p>
            </div>
          </header>

          <div class="st-logo">
            <div
              class="st-drop"
              :class="{ 'is-drag': dragging, 'has-logo': !!logoPreview }"
              role="button"
              tabindex="0"
              aria-label="Importer un logo (cliquer ou déposer une image)"
              @click="fileInput?.click()"
              @keydown.enter.prevent="fileInput?.click()"
              @keydown.space.prevent="fileInput?.click()"
              @dragover.prevent="dragging = true"
              @dragleave.prevent="dragging = false"
              @drop.prevent="onDrop"
            >
              <img v-if="logoPreview" :src="logoPreview" alt="Logo de la société" />
              <span v-else class="st-drop-empty">
                <i class="ph ph-image-square" aria-hidden="true"></i>
                Aucun logo
              </span>
              <span class="st-drop-hint"><i class="ph ph-upload-simple" aria-hidden="true"></i> Changer</span>
            </div>
            <div class="st-logo-text">
              <strong>Logo</strong>
              <p>Glissez une image ici ou cliquez pour choisir un fichier. PNG transparent recommandé, 2 Mo maximum.</p>
              <div class="st-logo-actions">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="fileInput?.click()">
                  <i class="ph ph-upload-simple" aria-hidden="true"></i> Importer
                </button>
                <button v-if="logoFile" type="button" class="btn btn-sm btn-light" @click="resetLogo">
                  Annuler le nouveau logo
                </button>
              </div>
              <span v-if="logoFile" class="st-badge"><i class="ph ph-dot-outline" aria-hidden="true"></i> Nouveau logo, pas encore enregistré</span>
            </div>
            <input
              ref="fileInput"
              class="visually-hidden"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              tabindex="-1"
              @change="onFileChangeLogo"
            />
          </div>

          <div class="st-fields">
            <div class="st-field st-field--wide">
              <label for="st-name">Nom de la société <span class="st-req" aria-hidden="true">*</span></label>
              <input
                id="st-name"
                ref="nameInput"
                v-model="form.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                maxlength="100"
                placeholder="Ex. GrowLogistics"
                required
                :aria-invalid="!!errors.name"
                aria-describedby="st-name-help"
                @blur="validateField('name')"
              />
              <div class="st-help" id="st-name-help">
                <span v-if="errors.name" class="st-err">{{ errors.name }}</span>
                <span class="st-count">{{ (form.name || "").length }}/100</span>
              </div>
            </div>
            <div class="st-field st-field--wide">
              <label for="st-website">Site web</label>
              <div class="st-input-icon">
                <i class="ph ph-globe" aria-hidden="true"></i>
                <input
                  id="st-website"
                  v-model="form.website"
                  type="url"
                  class="form-control"
                  :class="{ 'is-invalid': errors.website }"
                  maxlength="255"
                  placeholder="https://exemple.com"
                  :aria-invalid="!!errors.website"
                  @blur="normalizeWebsite(); validateField('website')"
                />
              </div>
              <div class="st-help">
                <span v-if="errors.website" class="st-err">{{ errors.website }}</span>
                <span v-else>https:// est ajouté automatiquement si besoin.</span>
              </div>
            </div>
          </div>
        </section>

        <!-- ── Brand colors ─────────────────────────────────────── -->
        <section class="st-card" aria-labelledby="st-colors">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-palette" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-colors">Couleurs de la plateforme</h2>
              <p>
                Appliquées à l'administration, à l'espace client et à l'application livreur. L'aperçu est immédiat ici ;
                les autres espaces les prennent au prochain chargement.
              </p>
            </div>
          </header>

          <div class="st-colors">
            <div v-for="c in colorFields" :key="c.key" class="st-color">
              <div class="st-color-head">
                <label :for="`st-${c.key}`">{{ c.label }}</label>
                <span>{{ c.hint }}</span>
              </div>

              <div class="st-color-row">
                <label class="st-swatch" :style="{ background: effectiveColor(c.key) }" :title="`Choisir la ${c.label.toLowerCase()}`">
                  <input
                    type="color"
                    :value="effectiveColor(c.key)"
                    :aria-label="`Choisir la ${c.label.toLowerCase()}`"
                    @input="onPickColor(c.key, $event)"
                  />
                </label>
                <input
                  :id="`st-${c.key}`"
                  v-model.trim="form[c.key]"
                  type="text"
                  class="form-control st-hex"
                  :class="{ 'is-invalid': errors[c.key] }"
                  maxlength="7"
                  spellcheck="false"
                  autocomplete="off"
                  :placeholder="`${c.default} (défaut)`"
                  :aria-invalid="!!errors[c.key]"
                  :aria-describedby="`st-${c.key}-help`"
                  @blur="normalizeColor(c.key); validateField(c.key)"
                />
                <button v-if="form[c.key]" type="button" class="btn btn-sm btn-light" @click="setColor(c.key, '')">
                  <i class="ph ph-arrow-counter-clockwise" aria-hidden="true"></i> Par défaut
                </button>
              </div>

              <div class="st-presets" role="group" :aria-label="`Couleurs suggérées — ${c.label.toLowerCase()}`">
                <button
                  v-for="p in c.presets"
                  :key="p"
                  type="button"
                  class="st-preset"
                  :class="{ 'is-active': effectiveColor(c.key) === p }"
                  :style="{ background: p }"
                  :aria-label="p"
                  :aria-pressed="effectiveColor(c.key) === p"
                  :title="p"
                  @click="setColor(c.key, p === c.default ? '' : p)"
                >
                  <i v-if="effectiveColor(c.key) === p" class="ph ph-check" aria-hidden="true"></i>
                </button>
              </div>

              <div :id="`st-${c.key}-help`" class="st-contrast" :class="colorCheck(c.key).ok ? 'is-ok' : 'is-bad'" aria-live="polite">
                <template v-if="colorCheck(c.key).valid">
                  <span class="st-contrast-sample" :style="{ background: effectiveColor(c.key) }">Aa</span>
                  <span v-if="colorCheck(c.key).ok">
                    Texte blanc lisible <b>{{ colorCheck(c.key).ratio }}:1</b>
                  </span>
                  <span v-else>
                    Trop clair : texte blanc peu lisible <b>{{ colorCheck(c.key).ratio }}:1</b> (min. {{ c.min }}:1)
                  </span>
                  <button v-if="!colorCheck(c.key).ok" type="button" class="st-contrast-fix" @click="fixColor(c.key)">
                    <i class="ph ph-magic-wand" aria-hidden="true"></i> Ajuster automatiquement
                  </button>
                </template>
                <span v-else class="st-err">{{ errors[c.key] || "Format attendu : #RRGGBB (ex. #2563EB)." }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- ── Contact ──────────────────────────────────────────── -->
        <section class="st-card" aria-labelledby="st-contact">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-address-book" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-contact">Coordonnées</h2>
              <p>Pour que vos clients et partenaires puissent vous joindre.</p>
            </div>
          </header>
          <div class="st-fields">
            <div class="st-field">
              <label for="st-email">Email</label>
              <div class="st-input-icon">
                <i class="ph ph-envelope-simple" aria-hidden="true"></i>
                <input
                  id="st-email"
                  v-model="form.email"
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': errors.email }"
                  maxlength="150"
                  placeholder="contact@exemple.com"
                  autocomplete="email"
                  :aria-invalid="!!errors.email"
                  @blur="validateField('email')"
                />
              </div>
              <div v-if="errors.email" class="st-help"><span class="st-err">{{ errors.email }}</span></div>
            </div>
            <div class="st-field">
              <label for="st-phone">Téléphone</label>
              <div class="st-input-icon">
                <i class="ph ph-phone" aria-hidden="true"></i>
                <input
                  id="st-phone"
                  v-model="form.phone"
                  type="tel"
                  class="form-control"
                  :class="{ 'is-invalid': errors.phone }"
                  maxlength="20"
                  placeholder="+212 600 000000"
                  autocomplete="tel"
                  :aria-invalid="!!errors.phone"
                  @blur="validateField('phone')"
                />
              </div>
              <div v-if="errors.phone" class="st-help"><span class="st-err">{{ errors.phone }}</span></div>
            </div>
            <div class="st-field">
              <label for="st-city">Ville du siège</label>
              <select id="st-city" v-model="form.siege" class="form-select">
                <option value="">— Choisir une ville —</option>
                <option v-for="option in cityOptions" :key="option" :value="option">{{ option }}</option>
              </select>
            </div>
            <div class="st-field st-field--wide">
              <label for="st-address">Adresse</label>
              <textarea
                id="st-address"
                v-model="form.adresse"
                class="form-control"
                rows="2"
                maxlength="500"
                placeholder="N°, rue, quartier…"
              ></textarea>
            </div>
          </div>
        </section>

        <!-- ── Preferences ──────────────────────────────────────── -->
        <section class="st-card" aria-labelledby="st-prefs">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-currency-circle-dollar" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-prefs">Devise</h2>
              <p>Affichée avec les montants (CRBT, frais, factures, tickets).</p>
            </div>
          </header>
          <div class="st-chips" role="radiogroup" aria-label="Devise">
            <button
              v-for="c in currencyOptions"
              :key="c"
              type="button"
              role="radio"
              class="st-chip"
              :class="{ 'is-active': form.devis === c }"
              :aria-checked="form.devis === c"
              @click="form.devis = c"
            >
              {{ c }}
            </button>
          </div>
        </section>

        <!-- ── Packaging fees (BL stock; own permission, own save) ─ -->
        <section v-if="canPackagingFees" class="st-card" aria-labelledby="st-packaging-fees">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-package" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-packaging-fees">Frais d'emballage (bon livraison stock)</h2>
              <p>Frais par colis proposés sur les bons de livraison stock. Un client avec des frais d'emballage personnalisés (fiche client) garde les siens.</p>
            </div>
          </header>
          <div class="st-field">
            <label for="st-packaging-fees-input">Frais généraux / colis</label>
            <div class="input-group">
              <input
                id="st-packaging-fees-input"
                v-model="packagingFees"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                :disabled="packagingFeesLoading"
                @keydown.enter.prevent="savePackagingFees"
              />
              <span class="input-group-text">{{ form.devis || "DH" }}</span>
              <button type="button" class="btn btn-primary" :disabled="savingPackagingFees || packagingFeesLoading" @click="savePackagingFees">
                <span v-if="savingPackagingFees" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <template v-else>Enregistrer</template>
              </button>
            </div>
          </div>
        </section>

        <!-- ── Bank ─────────────────────────────────────────────── -->
        <!-- ── Refund service fees (own permission, own save) ───── -->
        <section v-if="canRefundFees" class="st-card" aria-labelledby="st-refund-fees">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-hand-coins" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-refund-fees">Frais de service des remboursements</h2>
              <p>Frais généraux appliqués aux nouvelles demandes de remboursement. Un client avec des frais personnalisés (fiche client) garde les siens.</p>
            </div>
          </header>
          <div class="st-field">
            <label for="st-refund-fees-input">Frais généraux</label>
            <div class="input-group">
              <input
                id="st-refund-fees-input"
                v-model="refundFees"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                :disabled="refundFeesLoading"
                @keydown.enter.prevent="saveRefundFees"
              />
              <span class="input-group-text">{{ form.devis || "DH" }}</span>
              <button type="button" class="btn btn-primary" :disabled="savingRefundFees || refundFeesLoading" @click="saveRefundFees">
                <span v-if="savingRefundFees" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <template v-else>Enregistrer</template>
              </button>
            </div>
          </div>
        </section>

        <section class="st-card" aria-labelledby="st-bank">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-bank" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-bank">Banque de la société</h2>
              <p>Utilisée pour les virements aux clients lors de la facturation.</p>
            </div>
          </header>
          <div class="st-fields">
            <div class="st-field st-field--wide">
              <label for="st-bank-select">Banque</label>
              <select
                id="st-bank-select"
                v-model="form.bank_id"
                class="form-select"
                :class="{ 'is-invalid': errors.bank_id }"
                :aria-invalid="!!errors.bank_id"
                aria-describedby="st-bank-help"
              >
                <option value="">— Aucune (pas de frais de virement) —</option>
                <option v-for="b in banks" :key="b.id" :value="String(b.id)">
                  {{ b.name }}{{ b.code ? ` (code ${b.code})` : "" }}
                </option>
              </select>
              <div class="st-help" id="st-bank-help">
                <span v-if="errors.bank_id" class="st-err">{{ errors.bank_id }}</span>
                <span v-else>
                  La banque du client est détectée à partir des 3 premiers chiffres de son RIB. Client de la même banque :
                  aucun frais. Client d'une autre banque : {{ bankFees }} {{ form.devis || "DH" }} de frais de virement
                  déduits de la facture.
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- ── Terms ────────────────────────────────────────────── -->
        <section class="st-card" aria-labelledby="st-terms">
          <header class="st-card-head">
            <span class="st-card-icon"><i class="ph ph-scroll" aria-hidden="true"></i></span>
            <div>
              <h2 id="st-terms">Conditions générales</h2>
              <p>Le texte accepté par vos clients.</p>
            </div>
          </header>
          <textarea
            id="st-conditions"
            v-model="form.conditions"
            class="form-control st-terms"
            rows="12"
            maxlength="20000"
            aria-label="Conditions générales"
            placeholder="Rédigez vos conditions générales…"
          ></textarea>
          <div class="st-help"><span></span><span class="st-count">{{ (form.conditions || "").length.toLocaleString("fr-FR") }}/20 000</span></div>
        </section>
      </div>

      <!-- ── Live preview ─────────────────────────────────────────── -->
      <aside class="st-aside" aria-label="Aperçu">
        <section class="st-card st-preview">
          <span class="st-eyebrow">Aperçu</span>
          <div class="st-brand">
            <div class="st-brand-logo">
              <img v-if="logoPreview" :src="logoPreview" alt="" />
              <span v-else>{{ initials }}</span>
            </div>
            <strong>{{ form.name || "Nom de la société" }}</strong>
            <a v-if="websiteHref" :href="websiteHref" target="_blank" rel="noopener noreferrer">{{ websiteLabel }}</a>
          </div>
          <ul class="st-preview-list">
            <li><i class="ph ph-envelope-simple" aria-hidden="true"></i>{{ form.email || "—" }}</li>
            <li><i class="ph ph-phone" aria-hidden="true"></i>{{ form.phone || "—" }}</li>
            <li><i class="ph ph-map-pin" aria-hidden="true"></i>{{ [form.adresse, form.siege].filter(Boolean).join(", ") || "—" }}</li>
            <li><i class="ph ph-bank" aria-hidden="true"></i>{{ bankName || "—" }}</li>
          </ul>
          <div class="st-ticket">
            <span>Exemple sur un ticket</span>
            <b>123 {{ form.devis || "" }}</b>
          </div>
          <div class="st-theme" aria-hidden="true">
            <span class="st-theme-label">Exemple d'interface</span>
            <div class="st-theme-nav">
              <span class="is-active"><i class="ph ph-package"></i> Colis</span>
              <span><i class="ph ph-truck"></i> Livraisons</span>
            </div>
            <div class="st-theme-actions">
              <span class="btn btn-sm btn-primary">Principal</span>
              <span class="btn btn-sm btn-secondary">Secondaire</span>
              <span class="st-theme-badge">Nouveau</span>
            </div>
          </div>
        </section>
      </aside>

      <!-- ── Sticky save bar ────────────────────────────────────── -->
      <transition name="st-bar">
        <div v-if="dirtyCount > 0 || saving" class="st-savebar" role="status">
          <span class="st-savebar-text">
            <i class="ph ph-pencil-simple-line" aria-hidden="true"></i>
            <b>{{ dirtyCount }}</b> modification{{ dirtyCount > 1 ? "s" : "" }} non enregistrée{{ dirtyCount > 1 ? "s" : "" }}
            <kbd>Ctrl</kbd>+<kbd>S</kbd>
          </span>
          <div class="st-savebar-actions">
            <button type="button" class="btn btn-sm btn-light" :disabled="saving" @click="resetForm">Annuler</button>
            <button type="submit" class="btn btn-sm btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm" role="status"></span>
              Enregistrer
            </button>
          </div>
        </div>
      </transition>
    </form>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import api from "@/utils/axios";
import { useAppToast } from "@/composables/useAppToast";
import { useSanitize } from "@/composables/useSanitize";
import { useSettingsStore } from "@/stores/settings";
import { useAuthStore } from "@/stores/auth";
import {
  DEFAULT_PRIMARY,
  DEFAULT_SECONDARY,
  MIN_CONTRAST_PRIMARY,
  MIN_CONTRAST_SECONDARY,
  applyBrandTheme,
  contrastWithWhite,
  fixContrast,
  normalizeHex,
} from "@/utils/brandTheme";
import SettingsNav from "./SettingsNav.vue";

const FIELDS = [
  "name", "website", "email", "phone", "siege", "adresse", "devis", "conditions", "bank_id",
  "primary_color", "secondary_color",
] as const;
type Field = (typeof FIELDS)[number];
type ColorField = "primary_color" | "secondary_color";

// Presets all keep white text readable (≥ the min contrast below)
const COLOR_FIELDS = [
  {
    key: "primary_color" as ColorField,
    label: "Couleur principale",
    hint: "Boutons, liens, menu actif, graphiques",
    default: DEFAULT_PRIMARY,
    min: MIN_CONTRAST_PRIMARY,
    presets: [DEFAULT_PRIMARY, "#E11D48", "#DB2777", "#7C3AED", "#2563EB", "#0284C7", "#0D9488", "#16A34A"],
  },
  {
    key: "secondary_color" as ColorField,
    label: "Couleur secondaire",
    hint: "Boutons secondaires, notifications",
    default: DEFAULT_SECONDARY,
    min: MIN_CONTRAST_SECONDARY,
    presets: [DEFAULT_SECONDARY, "#0F172A", "#374151", "#1E3A8A", "#312E81", "#134E4A", "#14532D", "#4C0519"],
  },
];

// Must match Invoice::INTERBANK_TRANSFER_FEES (Back)
const INTERBANK_TRANSFER_FEES = 10;

// Logo is resized in the browser (keeps the ratio). The former 200×60 limit
// made the logo blurry on printed tickets.
const LOGO_MAX_W = 800;
const LOGO_MAX_H = 240;
const LOGO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const LOGO_MAX_MB = 2;

export default defineComponent({
  name: "AccountSettings",
  components: { SettingsNav },
  setup() {
    const { showToast } = useAppToast();
    const { sanitizeUrl } = useSanitize();
    const settingsStore = useSettingsStore();
    const auth = useAuthStore();

    // general refund service fees: separate permission, saved on their own
    const canRefundFees = computed(() => auth.can("REFUND_FEES"));
    const refundFees = ref<number | string>(0);
    const refundFeesLoading = ref(false);
    const savingRefundFees = ref(false);
    async function loadRefundFees() {
      if (!canRefundFees.value) return;
      refundFeesLoading.value = true;
      try {
        refundFees.value = (await api.get("/api/auth/refund-requests/settings")).data.fees;
      } catch (e) {
        showToast("Frais de remboursement indisponibles.", "error");
      } finally {
        refundFeesLoading.value = false;
      }
    }
    async function saveRefundFees() {
      const value = Number(refundFees.value);
      if (refundFees.value === "" || !isFinite(value) || value < 0) {
        showToast("Frais de service invalides.", "error");
        return;
      }
      savingRefundFees.value = true;
      try {
        const { data } = await api.post("/api/auth/refund-requests/settings", { fees: value });
        refundFees.value = data.fees;
        showToast("Frais de service enregistrés.", "success");
      } catch (e: any) {
        showToast(e?.response?.data?.message || "Enregistrement impossible.", "error");
      } finally {
        savingRefundFees.value = false;
      }
    }
    onMounted(loadRefundFees);

    // general packaging fees (BL stock): separate permission, saved on their own
    const canPackagingFees = computed(() => auth.can("PACKAGING_FEES"));
    const packagingFees = ref<number | string>(0);
    const packagingFeesLoading = ref(false);
    const savingPackagingFees = ref(false);
    async function loadPackagingFees() {
      if (!canPackagingFees.value) return;
      packagingFeesLoading.value = true;
      try {
        packagingFees.value = (await api.get("/api/auth/bls/packaging-fees")).data.fees;
      } catch (e) {
        showToast("Frais d'emballage indisponibles.", "error");
      } finally {
        packagingFeesLoading.value = false;
      }
    }
    async function savePackagingFees() {
      const value = Number(packagingFees.value);
      if (packagingFees.value === "" || !isFinite(value) || value < 0) {
        showToast("Frais d'emballage invalides.", "error");
        return;
      }
      savingPackagingFees.value = true;
      try {
        const { data } = await api.post("/api/auth/bls/packaging-fees", { fees: value });
        packagingFees.value = data.fees;
        showToast("Frais d'emballage enregistrés.", "success");
      } catch (e: any) {
        showToast(e?.response?.data?.message || "Enregistrement impossible.", "error");
      } finally {
        savingPackagingFees.value = false;
      }
    }
    onMounted(loadPackagingFees);

    const loading = ref(true);
    const loadError = ref(false);
    const saving = ref(false);
    const cities = ref<{ id: number; name: string }[]>([]);
    const banks = ref<{ id: number; name: string; code: string | null }[]>([]);

    const form = reactive<Record<Field, string>>({
      name: "", website: "", email: "", phone: "", siege: "", adresse: "", devis: "", conditions: "", bank_id: "",
      primary_color: "", secondary_color: "",
    });
    const saved = ref<Record<Field, string>>({ ...form });
    const errors = reactive<Partial<Record<Field, string>>>({});

    const logoPreview = ref<string | null>(null);
    const savedLogo = ref<string | null>(null);
    const logoFile = ref<File | null>(null);
    const fileInput = ref<HTMLInputElement | null>(null);
    const nameInput = ref<HTMLInputElement | null>(null);
    const dragging = ref(false);

    const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));

    async function load() {
      loading.value = true;
      loadError.value = false;
      try {
        const [settingsRes, citiesRes, banksRes] = await Promise.allSettled([
          api.get(`/api/auth/general_list`),
          api.get(`/api/auth/citiesList`),
          api.get(`/api/auth/banks`),
        ]);
        if (settingsRes.status !== "fulfilled") throw settingsRes.reason;
        const data = settingsRes.value.data || {};
        FIELDS.forEach((f) => (form[f] = str(data[f])));
        saved.value = { ...form };
        logoPreview.value = data.logo || null;
        savedLogo.value = data.logo || null;
        logoFile.value = null;
        if (citiesRes.status === "fulfilled") cities.value = citiesRes.value.data || [];
        if (banksRes.status === "fulfilled") banks.value = banksRes.value.data || [];
      } catch (error) {
        console.error("Error fetching settings:", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    }

    // Keep the current city/currency selectable even if not in the lists
    const cityOptions = computed(() => {
      const names = cities.value.map((c) => c.name);
      if (form.siege && !names.includes(form.siege)) names.unshift(form.siege);
      return names;
    });
    const currencyOptions = computed(() => {
      const base = ["Dhs", "MAD", "EUR", "USD"];
      if (saved.value.devis && !base.includes(saved.value.devis)) base.unshift(saved.value.devis);
      return base;
    });

    // ── Validation ──
    const stripTags = (v: string) => (v || "").replace(/<\/?[a-zA-Z][^>]*>/g, "").trim();

    function normalizeWebsite() {
      const v = (form.website || "").trim();
      if (v && !/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) form.website = `https://${v}`;
    }

    function validateField(field: Field) {
      delete errors[field];
      const v = (form[field] || "").trim();
      if (field === "name") {
        if (!v) errors.name = "Le nom de la société est obligatoire.";
        else if (v.length > 100) errors.name = "Maximum 100 caractères.";
      }
      if (field === "email" && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) errors.email = "Adresse email invalide.";
      if (field === "phone" && v && !/^[\d\s+\-().]{6,20}$/.test(v))
        errors.phone = "Numéro invalide (chiffres, espaces, + et - uniquement).";
      if (field === "website" && v) {
        const safe = sanitizeUrl(v);
        if (!safe || !/^https?:\/\/[^\s.]+\.[^\s]+$/i.test(safe)) errors.website = "Adresse web invalide.";
      }
      if ((field === "primary_color" || field === "secondary_color") && v) {
        const check = colorCheck(field);
        if (!check.valid) errors[field] = "Format attendu : #RRGGBB (ex. #2563EB).";
        else if (!check.ok) errors[field] = "Couleur trop claire : le texte blanc ne serait pas lisible dessus.";
      }
    }
    function validateForm() {
      normalizeWebsite();
      COLOR_FIELDS.forEach((c) => normalizeColor(c.key));
      (["name", "email", "phone", "website", "primary_color", "secondary_color"] as Field[]).forEach(validateField);
      return Object.keys(errors).length === 0;
    }

    // ── Dirty tracking ──
    const dirtyCount = computed(
      () => FIELDS.filter((f) => (form[f] || "") !== (saved.value[f] || "")).length + (logoFile.value ? 1 : 0)
    );
    function resetForm() {
      FIELDS.forEach((f) => (form[f] = saved.value[f]));
      Object.keys(errors).forEach((k) => delete errors[k as Field]);
      resetLogo();
    }

    // ── Brand colors ──
    // "" = default theme; the swatch/preview show the default color then
    const colorMeta = (key: ColorField) => COLOR_FIELDS.find((c) => c.key === key) || COLOR_FIELDS[0];
    const effectiveColor = (key: ColorField) => normalizeHex(form[key]) || colorMeta(key).default;
    function colorCheck(key: ColorField) {
      const valid = !form[key] || !!normalizeHex(form[key]);
      const ratio = valid ? contrastWithWhite(effectiveColor(key)) : 0;
      return { valid, ok: valid && ratio >= colorMeta(key).min - 0.01, ratio: ratio.toFixed(1).replace(".", ",") };
    }
    function setColor(key: ColorField, value: string) {
      form[key] = value;
      delete errors[key];
    }
    function onPickColor(key: ColorField, event: Event) {
      setColor(key, (event.target as HTMLInputElement).value.toUpperCase());
    }
    function normalizeColor(key: ColorField) {
      const hex = normalizeHex(form[key]);
      if (hex) form[key] = hex;
    }
    function fixColor(key: ColorField) {
      setColor(key, fixContrast(effectiveColor(key), colorMeta(key).min));
    }

    // Live preview on the whole app (not cached until saved). The native
    // picker fires on every mouse move, so updates are batched per frame.
    let previewFrame = 0;
    let previewed = false;
    const lastValid: Record<ColorField, string> = { primary_color: "", secondary_color: "" };
    watch(
      () => [form.primary_color, form.secondary_color],
      () => {
        COLOR_FIELDS.forEach(({ key }) => {
          if (!form[key] || normalizeHex(form[key])) lastValid[key] = form[key]; // ignore half-typed values
        });
        previewed = true;
        cancelAnimationFrame(previewFrame);
        previewFrame = requestAnimationFrame(() => applyBrandTheme(lastValid.primary_color, lastValid.secondary_color, false));
      }
    );

    // ── Logo ──
    function resetLogo() {
      logoFile.value = null;
      logoPreview.value = savedLogo.value;
      if (fileInput.value) fileInput.value.value = "";
    }
    function handleLogoFile(file: File | undefined | null) {
      if (!file) return;
      if (!LOGO_TYPES.includes(file.type)) {
        showToast("Format non autorisé. Utilisez JPG, PNG, WEBP ou GIF.", "error");
        return;
      }
      if (file.size > LOGO_MAX_MB * 1024 * 1024) {
        showToast(`L'image ne doit pas dépasser ${LOGO_MAX_MB} Mo.`, "error");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const ratio = Math.min(1, LOGO_MAX_W / img.width, LOGO_MAX_H / img.height);
          const w = Math.round(img.width * ratio);
          const h = Math.round(img.height * ratio);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          (canvas.getContext("2d") as CanvasRenderingContext2D).drawImage(img, 0, 0, w, h);
          logoPreview.value = canvas.toDataURL("image/png");
          canvas.toBlob((blob) => {
            if (blob) logoFile.value = new File([blob], "logo.png", { type: "image/png" });
          }, "image/png");
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
    const onFileChangeLogo = (event: Event) => {
      const input = event.target as HTMLInputElement;
      handleLogoFile(input.files?.[0]);
      input.value = "";
    };
    function onDrop(event: DragEvent) {
      dragging.value = false;
      handleLogoFile(event.dataTransfer?.files?.[0]);
    }

    // ── Save ──
    async function submitForm() {
      if (saving.value) return;
      if (!validateForm()) {
        showToast("Corrigez les champs en rouge avant d'enregistrer.", "error");
        await nextTick();
        (document.querySelector(".st .is-invalid") as HTMLElement | null)?.focus();
        return;
      }
      saving.value = true;
      try {
        const formData = new FormData();
        // every field is sent, so a field can also be emptied
        FIELDS.forEach((f) => formData.append(f, f === "website" ? sanitizeUrl(form[f] || "") : stripTags(form[f])));
        if (logoFile.value) formData.append("logo", logoFile.value);

        const response = await api.post("/api/auth/general_add", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (response.data?.success) {
          const data = response.data.data || {};
          FIELDS.forEach((f) => (form[f] = str(data[f] ?? form[f])));
          saved.value = { ...form };
          if (data.logo) {
            savedLogo.value = data.logo;
            logoPreview.value = data.logo;
          }
          logoFile.value = null;
          applyBrandTheme(form.primary_color, form.secondary_color); // cache the saved colors
          settingsStore.fetchSettings(); // refresh logo/name in the layout
          showToast("Paramètres enregistrés.", "success");
        } else {
          showToast(response.data?.message || "L'enregistrement a échoué.", "error");
        }
      } catch (error: any) {
        const res = error?.response;
        if (res?.status === 422 && res.data?.errors) {
          Object.entries(res.data.errors).forEach(([k, v]) => {
            if ((FIELDS as readonly string[]).includes(k)) errors[k as Field] = (v as string[])[0];
          });
        }
        showToast(res?.data?.message || "Une erreur est survenue lors de l'enregistrement.", "error");
      } finally {
        saving.value = false;
      }
    }

    // Ctrl/Cmd + S
    function onKeydown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (dirtyCount.value > 0) submitForm();
      }
    }
    // Don't lose unsaved changes
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (dirtyCount.value > 0) {
        event.preventDefault();
        event.returnValue = "";
      }
    }
    onBeforeRouteLeave(() => {
      if (dirtyCount.value > 0 && !saving.value) {
        return window.confirm("Vous avez des modifications non enregistrées. Quitter quand même ?");
      }
      return true;
    });

    onMounted(() => {
      load();
      document.addEventListener("keydown", onKeydown);
      window.addEventListener("beforeunload", onBeforeUnload);
    });
    onBeforeUnmount(() => {
      // leaving without saving: back to the saved colors
      cancelAnimationFrame(previewFrame);
      if (previewed) applyBrandTheme(saved.value.primary_color, saved.value.secondary_color, false);
      document.removeEventListener("keydown", onKeydown);
      window.removeEventListener("beforeunload", onBeforeUnload);
    });

    // ── Preview helpers ──
    const initials = computed(
      () =>
        (form.name || "?")
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((p) => p[0] || "")
          .join("")
          .toUpperCase() || "?"
    );
    const websiteHref = computed(() => {
      const v = sanitizeUrl((form.website || "").trim());
      if (!v) return "";
      return /^https?:\/\//i.test(v) ? v : `https://${v}`;
    });
    const websiteLabel = computed(() => websiteHref.value.replace(/^https?:\/\//i, "").replace(/\/$/, ""));
    const bankName = computed(() => banks.value.find((b) => String(b.id) === form.bank_id)?.name || "");

    return {
      canRefundFees,
      canPackagingFees,
      packagingFees,
      packagingFeesLoading,
      savingPackagingFees,
      savePackagingFees,
      refundFees,
      refundFeesLoading,
      savingRefundFees,
      saveRefundFees,
      loading,
      loadError,
      saving,
      form,
      errors,
      cityOptions,
      currencyOptions,
      banks,
      bankName,
      bankFees: INTERBANK_TRANSFER_FEES,
      colorFields: COLOR_FIELDS,
      effectiveColor,
      colorCheck,
      setColor,
      onPickColor,
      normalizeColor,
      fixColor,
      logoPreview,
      logoFile,
      fileInput,
      nameInput,
      dragging,
      dirtyCount,
      initials,
      websiteHref,
      websiteLabel,
      load,
      validateField,
      normalizeWebsite,
      resetForm,
      resetLogo,
      onFileChangeLogo,
      onDrop,
      submitForm,
    };
  },
});
</script>

<style scoped>
.st {
  padding-bottom: var(--ui-space-6);
}
.st-head {
  margin-bottom: var(--ui-space-5);
}
.st-eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-eyebrow);
  text-transform: uppercase;
}
.st-title {
  margin: 0;
  font-size: clamp(1.6rem, 2.6vw, 2.2rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
}
.st-sub {
  max-width: 680px;
  margin: 6px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}

.st-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: var(--ui-space-5);
  align-items: start;
}
.st-main {
  display: flex;
  flex-direction: column;
  gap: var(--ui-space-5);
  min-width: 0;
}
.st-aside {
  position: sticky;
  top: calc(var(--ui-frame-gap) + var(--ui-topbar-height) + 20px);
}
.st-card {
  padding: var(--ui-space-6);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.st-card-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: var(--ui-space-5);
}
.st-card-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-size: 21px;
}
.st-card-head h2 {
  margin: 0;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.st-card-head p {
  margin: 2px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}

/* Logo */
.st-logo {
  display: flex;
  align-items: center;
  gap: var(--ui-space-5);
  margin-bottom: var(--ui-space-5);
  padding-bottom: var(--ui-space-5);
  border-bottom: 1px dashed var(--ui-border-strong);
}
.st-drop {
  position: relative;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 220px;
  height: 110px;
  padding: 12px;
  border: 2px dashed var(--ui-border-strong);
  border-radius: var(--ui-radius-lg);
  background:
    repeating-conic-gradient(var(--ui-surface-sunken) 0% 25%, var(--ui-surface) 0% 50%) 50% / 16px 16px;
  cursor: pointer;
  overflow: hidden;
  outline: none;
  transition: border-color var(--ui-dur) var(--ui-ease), box-shadow var(--ui-dur) var(--ui-ease);
}
.st-drop:hover,
.st-drop:focus-visible,
.st-drop.is-drag {
  border-color: var(--ui-accent);
  box-shadow: var(--ui-focus-ring);
}
.st-drop img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.st-drop-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.st-drop-empty i {
  font-size: 28px;
}
.st-drop-hint {
  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px;
  background: rgba(28, 25, 23, 0.72);
  color: #ffffff;
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  transform: translateY(100%);
  transition: transform var(--ui-dur) var(--ui-ease);
}
.st-drop:hover .st-drop-hint,
.st-drop:focus-visible .st-drop-hint {
  transform: none;
}
.st-logo-text strong {
  font-size: var(--ui-fs-base);
  font-weight: var(--ui-fw-black);
}
.st-logo-text p {
  margin: 4px 0 10px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.st-logo-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.st-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 10px;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}

/* Fields */
.st-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--ui-space-4) var(--ui-space-5);
}
.st-field--wide {
  grid-column: 1 / -1;
}
.st-field label {
  display: block;
  margin-bottom: 6px;
  color: var(--ui-text);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.st-req {
  color: var(--ui-danger);
}
.st-input-icon {
  position: relative;
}
.st-input-icon > i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-faint);
  font-size: 17px;
  pointer-events: none;
}
.st-input-icon .form-control {
  padding-left: 42px;
}
.st-help {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.st-err {
  color: var(--ui-danger);
  font-weight: var(--ui-fw-semibold);
}
.st-count {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}
.st-terms {
  min-height: 260px;
  font-size: var(--ui-fs-sm);
  line-height: 1.6;
  resize: vertical;
}

/* Brand colors */
.st-colors {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--ui-space-5);
}
.st-color {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: var(--ui-space-4);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
}
.st-color-head label {
  display: block;
  color: var(--ui-text);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.st-color-head span {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.st-color-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.st-swatch {
  position: relative;
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: var(--ui-radius-sm);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: transform var(--ui-dur-fast) var(--ui-ease);
}
.st-swatch:hover {
  transform: scale(1.05);
}
.st-swatch:focus-within {
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12), var(--ui-focus-ring);
}
/* native picker stays clickable/focusable but invisible over the swatch */
.st-swatch input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.st-hex {
  flex: 1 1 auto;
  min-width: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  text-transform: uppercase;
}
.st-hex::placeholder {
  text-transform: none;
}
.st-color-row .btn {
  flex: 0 0 auto;
  white-space: nowrap;
}
.st-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.st-preset {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 2px solid var(--ui-surface);
  border-radius: 50%;
  box-shadow: 0 0 0 1px var(--ui-border-strong);
  color: #ffffff;
  font-size: 14px;
  transition: transform var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.st-preset:hover {
  transform: scale(1.12);
}
.st-preset.is-active {
  box-shadow: 0 0 0 2px var(--ui-text);
}
.st-preset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--ui-text), var(--ui-focus-ring);
}
.st-contrast {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.st-contrast.is-ok b {
  color: var(--ui-success);
}
.st-contrast.is-bad {
  color: var(--ui-danger);
  font-weight: var(--ui-fw-semibold);
}
.st-contrast-sample {
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 22px;
  border-radius: 6px;
  color: #ffffff;
  font-weight: var(--ui-fw-black);
}
.st-contrast-fix {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid currentColor;
  border-radius: var(--ui-radius-pill);
  background: transparent;
  color: var(--ui-danger);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
}
.st-contrast-fix:hover {
  background: var(--ui-danger-soft);
}

/* Theme preview (aside) */
.st-theme {
  margin-top: 14px;
  padding: 12px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
}
.st-theme-label {
  display: block;
  margin-bottom: 8px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}
.st-theme-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}
.st-theme-nav span {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: var(--ui-radius-xs);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.st-theme-nav span.is-active {
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.st-theme-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.st-theme-actions .btn {
  pointer-events: none;
}
.st-theme-badge {
  padding: 3px 9px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-heavy);
}

/* Currency chips */
.st-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.st-chip {
  min-width: 72px;
  padding: 10px 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.st-chip:hover {
  border-color: var(--ui-accent);
  color: var(--ui-accent-text);
}
.st-chip.is-active {
  border-color: var(--ui-accent);
  background: var(--ui-accent);
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.28);
}

/* Preview */
.st-preview {
  padding: var(--ui-space-5);
}
.st-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 10px 0 14px;
  padding: 18px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  text-align: center;
}
.st-brand-logo {
  display: grid;
  place-items: center;
  width: 100%;
  height: 70px;
}
.st-brand-logo img {
  max-width: 180px;
  max-height: 70px;
  object-fit: contain;
}
.st-brand-logo span {
  display: grid;
  place-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--ui-accent);
  color: #ffffff;
  font-size: 20px;
  font-weight: var(--ui-fw-black);
}
.st-brand strong {
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
  word-break: break-word;
}
.st-brand a {
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  text-decoration: none;
}
.st-preview-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 14px;
  padding: 0;
  list-style: none;
  font-size: var(--ui-fs-sm);
}
.st-preview-list li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--ui-text-secondary);
  word-break: break-word;
}
.st-preview-list i {
  margin-top: 2px;
  color: var(--ui-accent-text);
}
.st-ticket {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: var(--ui-radius-md);
  background: #1c1917;
  color: #ffffff;
}
.st-ticket span {
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
  opacity: 0.8;
}
.st-ticket b {
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
}

/* Save bar */
.st-savebar {
  position: sticky;
  bottom: 16px;
  z-index: 20;
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid rgba(var(--ui-accent-rgb), 0.4);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-elevated);
  box-shadow: var(--ui-shadow-pop);
}
.st-savebar-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.st-savebar-text i {
  color: var(--ui-accent-text);
  font-size: 18px;
}
.st-savebar-text kbd {
  padding: 1px 6px;
  border: 1px solid var(--ui-border-strong);
  border-radius: 5px;
  background: var(--ui-surface-sunken);
  color: var(--ui-text-muted);
  font-family: var(--ui-font);
  font-size: var(--ui-fs-micro);
  box-shadow: none;
}
.st-savebar-text kbd:first-of-type {
  margin-left: 6px;
}
.st-savebar-actions {
  display: flex;
  gap: 8px;
}
.st-bar-enter-active,
.st-bar-leave-active {
  transition: opacity var(--ui-dur) var(--ui-ease), transform var(--ui-dur) var(--ui-ease-spring);
}
.st-bar-enter-from,
.st-bar-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* Error / loading */
.st-error {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: var(--ui-space-5);
  border: 1px solid rgba(var(--ui-danger-rgb), 0.35);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-danger-soft);
}
.st-error i {
  color: var(--ui-danger);
  font-size: 28px;
}
.st-error p {
  margin: 2px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.st-error .btn {
  margin-left: auto;
}
.st-skel {
  display: block;
  margin-bottom: 14px;
  border-radius: var(--ui-radius-sm);
  background: linear-gradient(90deg, var(--ui-surface-sunken), var(--ui-surface-hover), var(--ui-surface-sunken));
  background-size: 300% 100%;
  animation: st-shimmer 1.2s ease-in-out infinite;
}
.st-skel--title { width: 40%; height: 20px; }
.st-skel--field { width: 100%; height: 46px; }
.st-skel--block { width: 100%; height: 240px; }
@keyframes st-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media (max-width: 1199.98px) {
  .st-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .st-aside {
    position: static;
    order: -1;
  }
}
@media (max-width: 767.98px) {
  .st-card {
    padding: var(--ui-space-4);
  }
  .st-fields,
  .st-colors {
    grid-template-columns: minmax(0, 1fr);
  }
  .st-logo {
    flex-direction: column;
    align-items: stretch;
  }
  .st-drop {
    width: 100%;
  }
  .st-savebar {
    flex-direction: column;
    align-items: stretch;
    border-radius: var(--ui-radius-lg);
  }
  .st-savebar-text kbd {
    display: none;
  }
  .st-savebar-actions .btn {
    flex: 1 1 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .st-skel { animation: none; }
}
</style>
